import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, TrendingUp, CalendarDays, BarChart3, Globe2, Smartphone, Languages } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0D0C0B] border border-[#F7F4EE]/15 focus:border-[#C5A059] text-[#F7F4EE] px-4 py-3.5 text-center text-lg tracking-[0.5em] outline-none transition-colors duration-300 placeholder:tracking-normal placeholder:text-[#C5BCAD]/50";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="border border-[#F7F4EE]/10 bg-[#1C1917] p-6">
    <Icon size={18} strokeWidth={1.5} className="text-[#C5A059]" />
    <p className="mt-4 font-cinzel text-4xl text-[#F7F4EE]">{value}</p>
    <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#C5BCAD]">{label}</p>
  </div>
);

const RankList = ({ title, icon: Icon, items, total, testid }) => (
  <div data-testid={testid} className="border border-[#F7F4EE]/10 bg-[#1C1917] p-6">
    <h3 className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">
      <Icon size={15} className="text-[#C5A059]" />
      {title}
    </h3>
    <ul className="mt-5 space-y-3.5">
      {items.map((it) => (
        <li key={it.name}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-[#F7F4EE] truncate">{it.name}</span>
            <span className="font-cinzel text-[#C5A059] shrink-0">{it.n}</span>
          </div>
          <div className="mt-1.5 h-1 bg-[#0D0C0B]">
            <div
              className="h-full bg-[#C5A059]/70"
              style={{ width: `${total ? Math.max(4, (it.n / total) * 100) : 0}%` }}
            />
          </div>
        </li>
      ))}
      {items.length === 0 && (
        <li className="text-sm text-[#C5BCAD]/60">Nessun dato ancora.</li>
      )}
    </ul>
  </div>
);

const Visite = () => {
  const [pin, setPin] = useState("");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/visits/stats?pin=${encodeURIComponent(pin)}`);
      if (!res.ok) throw new Error();
      setStats(await res.json());
    } catch {
      setError("PIN non valido. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const maxDay = stats ? Math.max(...stats.daily.map((d) => d.n), 1) : 1;
  const weekTotal = stats ? stats.daily.reduce((a, d) => a + d.n, 0) : 0;
  const avg = stats ? (weekTotal / 7).toFixed(1) : 0;

  return (
    <div data-testid="visits-page" className="min-h-screen bg-[#0D0C0B] text-[#F7F4EE] px-5 sm:px-8 py-16">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <img src="/img/logo.png" alt="John Tartarino" className="h-12 w-auto" />
          <a href="/" data-testid="visits-back-home" className="text-xs uppercase tracking-[0.2em] text-[#C5BCAD] hover:text-[#C5A059] transition-colors">
            Torna al sito
          </a>
        </div>

        {!stats ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-24 max-w-sm mx-auto text-center"
          >
            <Lock size={26} strokeWidth={1.5} className="mx-auto text-[#C5A059]" />
            <h1 className="mt-6 font-display text-3xl">Area riservata</h1>
            <p className="mt-3 text-sm text-[#C5BCAD]">
              Inserisci il codice per vedere le visite del sito.
            </p>
            <form onSubmit={submit} data-testid="visits-pin-form" className="mt-8 space-y-4">
              <input
                data-testid="visits-pin-input"
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Codice"
                className={inputCls}
                autoFocus
              />
              {error && (
                <p data-testid="visits-error" className="text-sm text-[#9E2A3F]">{error}</p>
              )}
              <button
                data-testid="visits-submit"
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-[#7A1C2E] text-sm uppercase tracking-[0.2em] hover:bg-[#9E2A3F] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Verifica..." : "Entra"}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-14">
            <h1 className="font-display text-3xl sm:text-4xl">
              Le visite del sito<span className="text-[#C5A059]">.</span>
            </h1>
            <p className="mt-2 text-sm text-[#C5BCAD]">
              Dati anonimi e aggregati: nessun cookie, nessun dato personale.
            </p>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={TrendingUp} label="Visite totali" value={stats.total} testid="visits-total" />
              <StatCard icon={CalendarDays} label="Oggi" value={stats.today} testid="visits-today" />
              <StatCard icon={BarChart3} label="Ultimi 7 giorni" value={weekTotal} testid="visits-week" />
              <StatCard icon={TrendingUp} label="Media al giorno" value={avg} testid="visits-avg" />
            </div>

            <div data-testid="visits-chart" className="mt-4 border border-[#F7F4EE]/10 bg-[#1C1917] p-6">
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">Andamento ultimi 7 giorni</h3>
              <div className="mt-6 flex items-end gap-2 sm:gap-3 h-40">
                {stats.daily.map((d) => (
                  <div key={d.d} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                    <span className="text-xs font-cinzel text-[#C5A059]">{d.n}</span>
                    <div
                      className="w-full bg-gradient-to-t from-[#7A1C2E] to-[#C5A059] transition-all duration-700"
                      style={{ height: `${Math.max(3, (d.n / maxDay) * 100)}%` }}
                    />
                    <span className="text-[10px] text-[#C5BCAD]/70">
                      {new Date(d.d + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 grid md:grid-cols-3 gap-4">
              <RankList title="Da dove arrivano" icon={Globe2} items={stats.referrers} total={stats.total} testid="visits-refs" />
              <RankList title="Dispositivi" icon={Smartphone} items={stats.devices} total={stats.total} testid="visits-devices" />
              <RankList title="Lingue del browser" icon={Languages} items={stats.languages} total={stats.total} testid="visits-langs" />
            </div>

            <p className="mt-8 text-xs text-[#C5BCAD]/60">
              Suggerimento: se molti arrivano da Google, investi in scheda e foto; se arrivano da Instagram, pubblica di più lì.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Visite;
